"use client";

import Image from "next/image";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import "@splidejs/react-splide/css";
import { toWebp } from "@/utils/common";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

const Card = ({ item, variant = "blue" }: any) => (
  <div
    className={`group relative w-[180px] h-[135px] overflow-hidden  border bg-white ${
      variant === "green" ? "border-[#B7E5C8]" : "border-[#B0D9F7]"
    }`}
  >
    {/* Hover Background: visible by default on touch-only devices, revealed on hover for mouse/trackpad devices */}
    <div
      className={`absolute inset-0 bg-cover bg-center bg-no-repeat opacity-100 pointer-fine:opacity-0 transition-opacity duration-300 pointer-fine:group-hover:opacity-100 ${
        variant === "green"
          ? "bg-[url('/images/ServicesPage/green-hover.jpg')]"
          : "bg-[url('/images/ServicesPage/blue-hover.jpg')]"
      }`}
    />

    {/* Content */}
    <div className="relative z-10 flex h-full flex-col justify-between px-3 py-2">
      <span className="xl:text-[20px] xl:leading-[28px] lg:text-[18px] lg:leading-[25.2px] font-bold pointer-fine:font-regular text-secondary-text transition-colors duration-300 pointer-fine:group-hover:font-bold">
        {item.name}
      </span>

      {item.icon && (
        <div className="flex justify-end">
          <Image
            src={item.icon}
            alt={item.name}
            width={48}
            height={48}
            className="opacity-100 pointer-fine:opacity-20 transition-all duration-300 pointer-fine:group-hover:opacity-100"
          />
        </div>
      )}
    </div>
  </div>
);

interface TechStackSectionProps {
  data?: any;
}

export default function TechStackSection({ data }: TechStackSectionProps) {
  const groups = data?.cards || [];

  const toTechList = (group: any) =>
    (group?.technologies || []).map((tech: any) => ({
      name: tech?.language,
      icon: tech?.Image?.data?.attributes?.url
        ? toWebp(`${baseUrl}${tech.Image.data.attributes.url}`)
        : undefined,
    }));

  const topTech = toTechList(groups[0]);
  const bottomTech = toTechList(groups[1] || groups[0]);

  return (
    <>
      <style jsx global>{`
        .tech-slider .splide__track {
          overflow: visible;
        }

        .tech-slider .splide__list {
          align-items: center;
        }

        /* Continuous linear animation */
        .tech-slider .splide__list {
          transition-timing-function: linear !important;
        }
      `}</style>

      <section className="overflow-hidden bg-white xl:py-[80px] lg:py-[60px] py-[40px]">
        {/* Top Slider */}
        {topTech.length > 0 && (
          <Splide
            className="tech-slider"
            extensions={{ AutoScroll }}
            options={{
              type: "loop",
              arrows: false,
              pagination: false,
              drag: false,
              autoWidth: true,
              gap: "20px",
              clones: 10,

              autoScroll: {
                speed: -0.5, // Left → Right (negative value)
                pauseOnHover: false,
                pauseOnFocus: false,
              },
            }}
          >
            {[...topTech, ...topTech, ...topTech].map((item, index) => (
              <SplideSlide key={index} className="!w-auto">
                <Card
                  item={item}
                  variant={index % 2 === 0 ? "blue" : "green"}
                />
              </SplideSlide>
            ))}
          </Splide>
        )}

        {/* Heading */}
        <div className="container">
          <h2 className="py-[40px] mx-auto max-w-[700px] text-center xl:text-[40px] xl:leading-[52px] lg:text-[48px] lg:leading-[46px] text-[30px] leading-[39px] font-bold tracking-[1px] text-black">
            {data?.title}
          </h2>

          {data?.sub_title && (
            <p className="mt-4 text-lg text-secondary-text">{data.sub_title}</p>
          )}
        </div>

        {/* Bottom Slider */}
        {bottomTech.length > 0 && (
          <Splide
            className="tech-slider"
            extensions={{ AutoScroll }}
            options={{
              type: "loop",
              arrows: false,
              pagination: false,
              drag: false,
              autoWidth: true,
              gap: "20px",
              clones: 10,

              autoScroll: {
                speed: 0.5, // Right → Left
                pauseOnHover: false,
                pauseOnFocus: false,
              },
            }}
          >
            {[...bottomTech, ...bottomTech, ...bottomTech].map(
              (item, index) => (
                <SplideSlide key={index} className="!w-auto">
                  <Card
                    item={item}
                    variant={index % 2 === 0 ? "blue" : "green"}
                  />
                </SplideSlide>
              ),
            )}
          </Splide>
        )}
      </section>
    </>
  );
}
